import { MapPin, Bed, Users, DollarSign, Trash2, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApartmentStatusBadge } from './ApartmentStatusBadge';
import { ApartmentRankControls } from './ApartmentRankControls';
import type { Apartment } from '../types/apartment';
import { PhotoImg } from './PhotoImg';

interface ApartmentCardProps {
  readonly apartment: Apartment;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly isRankingDisabled: boolean;
  readonly publishPending: boolean;
  readonly unpublishPending: boolean;
  readonly deletePending: boolean;
  readonly actionError: string | null;
  readonly onDelete: (id: string) => void;
  readonly onPublish: (id: string) => void;
  readonly onUnpublish: (id: string) => void;
  readonly onRankError: (message: string) => void;
}

export const ApartmentCard = ({
  apartment,
  isFirst,
  isLast,
  isRankingDisabled,
  publishPending,
  unpublishPending,
  deletePending,
  actionError,
  onDelete,
  onPublish,
  onUnpublish,
  onRankError,
}: ApartmentCardProps) => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges']);
  const navigate = useNavigate();

  return (
    <div className="card group hover:shadow-lg min-h-[280px] flex flex-col">
      {/* Photo */}
      {apartment.photos && apartment.photos.length > 0 && (
        <div className="relative">
          <PhotoImg
            src={apartment.photos[0].thumbnailUrl || apartment.photos[0].photoUrl}
            fallbackSrc={apartment.photos[0].photoUrl}
            alt={apartment.name}
            className="w-full h-32 object-cover rounded-t-lg"
          />
          {apartment.photos.length > 1 && (
            <span className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
              {t('common:morePhotos_other', { count: apartment.photos.length - 1 })}
            </span>
          )}
        </div>
      )}

      {/* Content */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-gray-900">{apartment.name}</h3>
            <ApartmentStatusBadge status={apartment.status} />
          </div>
          <p className="text-sm text-gray-600 line-clamp-2">{apartment.description}</p>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin size={16} className="text-primary-500" />
          <span>{apartment.address.city}, {apartment.address.country}</span>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Bed size={16} className="text-primary-500" />
            <span>{apartment.details.bedrooms} {t('common:beds')}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users size={16} className="text-primary-500" />
            <span>{apartment.details.maxGuests} {t('common:guests')}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2 border-t">
          <DollarSign size={20} className="text-accent-500" />
          <span className="text-xl font-bold text-gray-900">€{apartment.details.pricePerNight}</span>
          <span className="text-sm text-gray-600">{t('common:perNight')}</span>
        </div>

        {/* Features */}
        {apartment.details.features && apartment.details.features.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-2">
            {apartment.details.features.slice(0, 3).map((feature) => (
              <span key={feature} className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {t(`badges:feature${feature.charAt(0) + feature.slice(1).toLowerCase().replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())}` as any)}
              </span>
            ))}
            {apartment.details.features.length > 3 && (
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                {t('common:moreItems_other', { count: apartment.details.features.length - 3 })}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Ranking controls */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t">
        <span className="text-xs text-gray-400">{t('forms:viewOrder')}: {apartment.viewOrder}</span>
        <ApartmentRankControls
          apartmentId={apartment.id}
          viewOrder={apartment.viewOrder}
          isFirst={isFirst}
          isLast={isLast}
          isRankingDisabled={isRankingDisabled}
          onError={onRankError}
        />
      </div>

      {/* Actions footer */}
      <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t">
        {(apartment.status === 'DRAFT' || apartment.status === 'UNPUBLISHED') ? (
          <button
            onClick={() => onPublish(apartment.id)}
            disabled={publishPending}
            className="flex items-center justify-center gap-2 py-2 px-3 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {publishPending && (
              <span className="inline-block w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
            )}
            <span>{t('pages:publish')}</span>
          </button>
        ) : (
          <button
            onClick={() => onUnpublish(apartment.id)}
            disabled={unpublishPending}
            className="flex items-center justify-center gap-2 py-2 px-3 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {unpublishPending && (
              <span className="inline-block w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
            )}
            <span>{t('pages:unpublish')}</span>
          </button>
        )}
        <button
          onClick={() => onDelete(apartment.id)}
          disabled={deletePending}
          className="flex items-center justify-center gap-2 py-2 px-3 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors font-medium"
        >
          <Trash2 size={16} />
          <span className="hidden sm:inline">{t('common:delete')}</span>
        </button>
      </div>

      {/* Manage button — navigates to hub */}
      <div className="mt-3 pt-3 border-t">
        <button
          onClick={() => navigate(`/apartments/${apartment.id}`)}
          aria-label={t('pages:manageApartment', { name: apartment.name })}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors font-medium text-sm min-h-[44px]"
        >
          <Settings size={16} />
          {t('pages:manage')}
        </button>
      </div>

      {/* Per-card inline error message */}
      {actionError && (
        <p className="text-sm text-red-600 mt-2 text-center">{actionError}</p>
      )}
    </div>
  );
};

export default ApartmentCard;
